import { Box, Paper, Stack } from "@mui/material";
import AMapCandidates from "components/amap/AMapCandidates";
import AMapMatch from "components/amap/AMapMatch";
import AMapNoMatch from "components/amap/AMapNoMatch";
import Icon from "components/icon/icon";
import TableView from "components/table-view/TableView";
import { useCallback, useState } from "react";
import { reverseGeocode } from "./address-data-regeo";

interface AddressEncodingPanelProps {
  data: any[];
  handleMove: (id: string, type?: string) => void;
  handleData: (data: any[]) => void;
  isShowBtn?: boolean;
  title: string;
  tooltip: string;
  type: "candidates" | "no_match" | "match";
}

const AddressEncodingPanel = ({
  data,
  handleMove,
  handleData,
  isShowBtn,
  title,
  tooltip,
  type
}: AddressEncodingPanelProps) => {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectOnMap, setSelectOnMap] = useState<any>(undefined);

  const handleRowClick = useCallback((item: any) => {
    setSelectedId(item.id);
  }, []);

  const handleMarkerClick = useCallback((id: string) => {
    setSelectedId(id);
  }, []);

  const handleMapClick = async (item: any) => {
    if (type !== "no_match" || !selectedId) {
      return;
    }

    const { longitude, latitude } = item;
    const regeoData = await reverseGeocode(longitude, latitude);

    if (regeoData) {
      const newData = data.map(d => {
        if (d.id === selectedId) {
          return {
            ...d,
            longitude,
            latitude,
            ...regeoData
          };
        }
        return d;
      });
      handleData(newData);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        height: "100%",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", fontSize: 13 }}>
        {title}
        <Box className="tooltip tooltip-bottom" data-tip={tooltip}>
          <Icon icon="info" style={{ color: "#666" }} />
        </Box>
      </Box>
      <Stack
        direction="row"
        spacing={2}
        alignItems="top"
        sx={{ overflow: "hidden", height: "100%" }}
      >
        <Paper
          sx={{
            width: "830px",
            flexShrink: 0,
            overflow: "hidden",
            boxShadow: "none",
            height: "100%",
            display: "flex",
            flexDirection: "column"
          }}
        >
          <TableView
            data={data}
            type={type}
            handleMove={handleMove}
            handleData={handleData}
            isShowBtn={isShowBtn}
            onRowClick={handleRowClick}
            selectedId={selectedId}
            setSelectOnMap={setSelectOnMap}
          />
        </Paper>
        <Paper
          sx={{
            flex: 1,
            display: "flex",
            justifyContent: "flex-end",
            border: selectOnMap ? "3px solid #0e8a3f" : "",
            borderRadius: 3,
          }}
        >
          {(() => {
            if (type === "candidates") {
              return <AMapCandidates data={data} selectedId={selectedId} />;
            } else if (type === "no_match") {
              return (
                <AMapNoMatch
                  data={data}
                  selectedId={selectedId}
                  onMarkerClick={handleMarkerClick}
                  selectOnMap={selectOnMap}
                  onMapClick={handleMapClick}
                  setSelectOnMap={setSelectOnMap}
                />
              );
            } else {
              return (
                <AMapMatch
                  data={data}
                  selectedId={selectedId}
                  onMarkerClick={handleMarkerClick}
                />
              );
            }
          })()}
        </Paper>
      </Stack>
    </Box>
  );
};

export default AddressEncodingPanel;
